<template>

    <GPTInlineBanner320Slot3 v-if="AccountController.isAdProviderEnabled('GPT_INLINE_BANNER_320_SLOT_3')"
        @removeAds="onOpenModal('REMOVE_ADS')"
    />

    <Bsu v-if="bsu_visible"
    />

    <MenuToggler
      :webmail_view="webmail_view"
    />

    <div class="main-container">
        <Sidebar
            :webmail_view=webmail_view
        />

        <SetupMail v-if="modal_visibility.SETTINGS_WIZARD === true"
            :webmail_view=webmail_view
            @setupModalClose="onCloseSettingsWizard()"
        />

        <WhatsNew v-if="modal_visibility.SETTINGS_WHATS_NEW === true"
            :webmail_view=webmail_view
        />

        <FoldersCreateModal v-if="folders_create_visible"
            :parent_folder='context_folder'
            @modalWindowClose='closeFoldersCreate()'
        />

        <FoldersEditModal v-if="folders_edit_visible"
            :selected_folder='context_folder'
            @modalWindowClose='closeFoldersEdit()'
        />

        <FoldersMoveModal v-if="folders_move_visible"
            :selected_folder='context_folder'
            @modalWindowClose='closeFoldersMove()'
        />

        <FoldersEmptyModal v-if="folders_empty_visible"
            :selected_folder='context_folder'
            @modalWindowClose='closeFoldersEmpty()'
        />

        <FoldersDeleteModal v-if="folders_delete_visible"
            :selected_folder='context_folder'
            @modalWindowClose='closeFoldersDelete()'
        />

        <RemoveAdsModal v-if="modal_visibility.REMOVE_ADS === true"
            @modalWindowClose="onCloseModal('REMOVE_ADS');"
        />

        <router-view
          :key="key"
          :webmail_view='webmail_view'
        />

    </div>
</template>

<script setup>
    import {ref, watch}                   from 'vue';
    import {onMounted, onUnmounted}       from 'vue'
    import {useRoute, useRouter}          from 'vue-router';
    import MenuToggler                    from '@/vue/Views/Webmail/Partials/MenuToggler.vue';
    import Sidebar                        from '@/vue/Views/Webmail/Partials/Sidebar.vue';
    import Bsu                            from '@/vue/Views/Webmail/Partials/Bsu.vue';
    import SetupMail                      from '@/vue/Views/Wizard/SetupMail.vue';
    import WhatsNew                       from '@/vue/Views/Wizard/WhatsNew.vue';
    import FoldersCreateModal             from '@/vue/Views/Folders/Modals/FoldersCreateModal.vue';
    import FoldersDeleteModal             from '@/vue/Views/Folders/Modals/FoldersDeleteModal.vue';
    import FoldersEditModal               from '@/vue/Views/Folders/Modals/FoldersEditModal.vue';
    import FoldersEmptyModal              from '@/vue/Views/Folders/Modals/FoldersEmptyModal.vue';
    import FoldersMoveModal               from '@/vue/Views/Folders/Modals/FoldersMoveModal.vue';
    import RemoveAdsModal                 from '@/vue/Views/Webmail/Modals/RemoveAdsModal.vue';
    import GPTInlineBanner320Slot3        from '@/vue/Views/Ads/GPTInlineBanner320Slot3.vue';

    import PushNotificationsController    from '@/js/Webmail/WebSockets/PushNotificationsController';
    import PreLoaderController            from '@/js/Webmail/PreLoaderController';
    import SAPOMailConfig                 from '@/SAPOMailConfig';
    import EventBroadcaster               from '@/js/Common/Events/EventBroadcaster';
    import Events                         from '@/js/Common/Events/Events';
    import WindowEvents                   from '@/js/Common/Events/WindowEvents';
    import ViewportController             from '@/js/Mailboxes/ViewportController';
    import AccountController              from '@/js/Webmail/AccountController';

    const route                     = useRoute();
    const router                    = useRouter();
    const key                       = ref(route.params.folder_id);
    const context_folder            = ref();
    const bsu_visible               = ref(false);
    const folders_create_visible    = ref(null);
    const folders_edit_visible      = ref(null);
    const folders_move_visible      = ref(null);
    const folders_empty_visible     = ref(null);
    const folders_delete_visible    = ref(null);
    const whats_new_displayed       = ref(false);

    /**
     *  New Sistem to handle modals, instead of having a variable for each modal,
     *  we have a single object that handles the visibility of all modals, and a context variable to pass data to the modals
     *
     * @todo migrate existing modals to this system and remove old variables,
     *       Create a const/Enum with all modal names to avoid typos and have better autocompletion
     */
    const modal_context    = ref(null);
    const modal_visibility = ref({
        'REMOVE_ADS': false,
        'SETTINGS_WIZARD': false,
        'SETTINGS_WHATS_NEW': false,
    });

    /**
     * @important we assign a key to the router to make sure we reload the view every time we change folder
     *            This way we assure that every time we change folder we have a clean slate, and everything resets to it's initial state
     *            Handling cleanups directly in the components is a nightmare!
     */
    watch(() => route.fullPath, (currentValue, oldValue) => {
        if (route.name !== 'messages-list' && route.name !== 'message-open') {
            key.value = null;
            return;
        }

        key.value = route.params.folder_id + new URLSearchParams(route.query).toString();
    });

    onMounted(async () => {
        EventBroadcaster.on([
            [Events.PUSH_NOTIFICATIONS_MESSAGE    , onPushNotificationsMessage],
            [Events.PUSH_NOTIFICATIONS_CLICK      , onPushNotificationsClick],
            [Events.APPLICATION_PRELOADER_REMOVED , onPreLoaderRemoved],
        ])

        WindowEvents.on([
            ['online'          , onOnline],
            ['visibilitychange', onVisibilityChange],
        ]);

        if (SAPOMailConfig.server.web_sockets.push_notifications.enabled === true) {
            PushNotificationsController.start();
        }

        PreLoaderController.remove();
    });

    onUnmounted(() => {
        EventBroadcaster.destroy([
            [Events.PUSH_NOTIFICATIONS_MESSAGE, onPushNotificationsMessage],
            [Events.PUSH_NOTIFICATIONS_CLICK  , onPushNotificationsClick],
            [Events.PRELOADER_REMOVED         , onPreLoaderRemoved],
        ])

        WindowEvents.destroy([
            ['online'          , onOnline],
            ['visibilitychange', onVisibilityChange],
        ]);
    })

    const onPreLoaderRemoved = () => {
        setBsuVisibility(true);
        openWizards();
    }

    const onOnline = () => {
        PushNotificationsController.retryConnection();
        ViewportController.fetchMessagesBackground(SAPOMailConfig.client.folders.alias.inbox);
    }

    const onVisibilityChange = () => {
        if (document.visibilityState === 'visible' && PushNotificationsController.status !== 'CONNECTED') {
        ViewportController.fetchMessagesBackground(SAPOMailConfig.client.folders.alias.inbox);
        }
    }

    const onPushNotificationsMessage = async (event) => {
        if (SAPOMailConfig.client.notifications.browser.incoming_message.disabled_folders[event.folder]) {
            return;
        }

        await ViewportController.fetchMessagesBackground(event.folder);
        ViewportController.sendBrowserNotification(event.folder);
    }

    const onPushNotificationsClick = (data) => {
        routeTo(`/messages/${data.folder_id}/${data.message_id}`);
    }

    const onCloseSettingsWizard = () => {
        if (SAPOMailConfig.server.settings.whats_new.display && AccountController.account.premium.allowed && !whats_new_displayed.value) {
            setTimeout(() => {
                setModalVisibility('SETTINGS_WHATS_NEW', true);
                setWhatsNewDisplayed(true);
            }, 400);
        }
    }

    const openWizards = () => {
        // if (SAPOMailConfig.server.settings.wizard.display) {
        //     setModalVisibility('SETTINGS_WIZARD', true);
        //     return;
        // }

        if (SAPOMailConfig.server.settings.whats_new.display && AccountController.account.premium.allowed) {
            setModalVisibility('SETTINGS_WHATS_NEW', true);
            return;
        }
    }

    const openFoldersCreate = (folder) => {
        context_folder.value         = folder;
        folders_create_visible.value = true;
    }

    const closeFoldersCreate = () => {
        folders_create_visible.value = false;
    }

    const openFoldersEdit = (folder) => {
        context_folder.value       = folder;
        folders_edit_visible.value = true;
    }

    const closeFoldersEdit = () => {
        folders_edit_visible.value = false;
    }

    const openFoldersMove = (folder) => {
        context_folder.value       = folder;
        folders_move_visible.value = true;
    }

    const closeFoldersMove = () => {
        folders_move_visible.value = false;
    }

    const openFoldersEmpty = (folder) => {
        context_folder.value        = folder;
        folders_empty_visible.value = true;
    }

    const closeFoldersEmpty = () => {
        folders_empty_visible.value = false;
    }

    const openFoldersDelete = (folder) => {
        context_folder.value         = folder;
        folders_delete_visible.value = true;
    }

    const closeFoldersDelete = () => {
        folders_delete_visible.value = false;
    }

    const setBsuVisibility = (value) => {
        bsu_visible.value = value;
    }

    const onOpenModal = (modal_name, context = null) => {
        if (context) {
            setModalContext(context);
        }

        setModalVisibility(modal_name, true);
    }

    const onCloseModal = (modal_name) => {
        setModalVisibility(modal_name, false);
        setModalContext(null);
    }

    const setModalVisibility = (modal_name, visibility) => {
        modal_visibility.value[modal_name] = visibility;
    }

    const setModalContext = (context) => {
        modal_context.value = context;
    }

    const setWhatsNewDisplayed = (value) => {
        whats_new_displayed.value = value;
    }

    const routeTo = (path, params) => {
        if (params) {
            path += '?' + new URLSearchParams(params).toString();
        }
        else if (route.query && params !== false) {
            path += '?' + new URLSearchParams(route.query).toString();
        }

        router.push(path);
    }

    const routeBack = () => {
        if (window.history.length > 1) {
            router.back();
        } else {
            router.push({name: 'messages'});
        }
    }

    const isAdProviderEnabled = (provider) => {
        return AccountController.isAdProviderEnabled(provider);
    };

    window.openModal = (modal_name, context = null) => {
        onOpenModal(modal_name, context);
    }

    const webmail_view = {
        openFoldersCreate   : openFoldersCreate,
        closeFoldersCreate  : closeFoldersCreate,
        openFoldersEdit     : openFoldersEdit,
        closeFoldersEdit    : closeFoldersEdit,
        openFoldersMove     : openFoldersMove,
        closeFoldersMove    : closeFoldersMove,
        openFoldersEmpty    : openFoldersEmpty,
        closeFoldersEmpty   : closeFoldersEmpty,
        openFoldersDelete   : openFoldersDelete,
        closeFoldersDelete  : closeFoldersDelete,
        openModal           : onOpenModal,
        closeModal          : onCloseModal,
        routeTo             : routeTo,
        routeBack           : routeBack,
    };
</script>
